import api from "./api";

const BASE = "/employee";

const safe = async (fn, fallback = null) => {
    try {
        const res = await fn();
        return res?.data;
    } catch (err) {
        console.warn("API error:", err?.response?.data ?? err?.message);
        return fallback;
    }
};

const employeeService = {
    /* EMPLOYEES */
    getAll: async () => {
        return safe(() => api.get(BASE), []);
    },

    create: async (data) => {
        return safe(() => api.post(BASE, data));
    },

    update: async (id, data) => {
        return safe(() => api.put(`${BASE}/${id}`, data));
    },

    remove: async (id) => {
        return safe(() => api.delete(`${BASE}/${id}`));
    },

    getById: async (id) => {
        return safe(() => api.get(`${BASE}/${id}`));
    },

    getByCompanyAndType: async (companyId, userType) => {
        return safe(
            () => api.get(`${BASE}/company/${companyId}/type/${userType}`),
            []
        );
    },

    autoEndContracts: async () => {
        return safe(() => api.post(`${BASE}/auto-end-contracts`));
    },

    autoTerminatePending: async () => {
        return safe(() => api.post(`${BASE}/auto-terminate-pending`));
    },

    /* SHIPMENTS */
    createShipmentForClient: async (clientId) => {
        return safe(() =>
            api.post("/shipment", {
                paymentStatus: 0,
                clientId,
            })
        );
    },

    getActiveShipments: async () => {
        return safe(() => api.get("/shipment/active"), []);
    },

    updateShipmentStatus: async (shipmentId, status) => {
        return safe(() =>
            api.put(`/shipment/${shipmentId}/status`, {
                status,
            })
        );
    },

    /* LEGAL ENTITY CLIENTS */
    getLegalEntityClients: async () => {
        return safe(() => api.get("/legalentity"), []);
    },

    getLegalEntityClientById: async (id) => {
        return safe(() => api.get(`/legalentity/${id}`));
    },

    /* SMART DELIVERY */
    processSmartDelivery: async (parcelId, distances) => {
        return safe(() =>
            api.post("/smartdelivery/process", {
                parcelId: Number(parcelId),
                distances,
            })
        );
    },

    /* GEOFENCE */
    getGeoFenceZones: async () => {
        return safe(() => api.get("/geofence"), []);
    },

    getGeoFenceZoneById: async (id) => {
        return safe(() => api.get(`/geofence/${id}`));
    },

    checkGeoFenceInternal: async (lat, lng) => {
        return safe(() =>
            api.get("/geofence/check/internal", {
                params: { lat, lng },
            })
        );
    },

    /* IOT DEVICES */
    getIoTDevices: async () => {
        return safe(() => api.get("/iotdevice"), []);
    },

    getIoTDeviceById: async (id) => {
        return safe(() => api.get(`/iotdevice/${id}`));
    },

    getIoTDeviceByTrackingNumber: async (trackingNumber) => {
        return safe(() => api.get(`/iotdevice/tracking/${trackingNumber}`));
    },

    getIoTDevicePosition: async (id) => {
        return safe(() => api.get(`/iotdevice/${id}/position`));
    },

    getIoTDeviceOnlineStatus: async (id) => {
        return safe(() => api.get(`/iotdevice/${id}/online`));
    },

    getIoTDeviceRoute: async (id) => {
        return safe(() => api.get(`/iotdevice/${id}/route`), []);
    },

    updateIoTTelemetry: async (deviceId, latitude, longitude) => {
        return safe(() =>
            api.post("/iotdevice/telemetry", {
                deviceId,
                latitude,
                longitude,
            })
        );
    },

    sendIoTHeartbeat: async (id) => {
        return safe(() => api.post(`/iotdevice/${id}/heartbeat`));
    },

    deleteIoTDevice: async (id) => {
        return safe(() => api.delete(`/iotdevice/${id}`));
    },
};

export default employeeService;